import { onMessage, MessagePayload } from 'firebase/messaging';
import { getMessagingInstance, initializeMessaging } from '../auth/firebase';
import i18n from '../i18n';

interface WebPushPayload extends MessagePayload {
  webpush?: {
    notification?: {
      icon?: string;
      badge?: string;
      tag?: string;
      requireInteraction?: boolean;
      vibrate?: number[];
    };
  };
}

/**
 * Dispatch a custom event for foreground notifications
 * This allows any component (like App.tsx) to listen and show a toast
 */
export function dispatchForegroundNotification(title: string, body: string, data?: Record<string, unknown>) {
  const event = new CustomEvent('foregroundNotification', {
    detail: { title, body, data, timestamp: Date.now() }
  });
  window.dispatchEvent(event);
  console.log('[Foreground] Dispatched custom event:', { title, body });
}

/**
 * Initialize foreground notification handling
 *
 * Firebase does NOT auto-display in foreground on web - we must manually display.
 * onMessage handler is required to intercept foreground messages.
 */
export function initializeForegroundNotifications() {
  const logs: string[] = [];

  logs.push('[Foreground] ====== INITIALIZING FOREGROUND HANDLER ======');
  console.log('[Foreground] ====== INITIALIZING FOREGROUND HANDLER ======');

  // Mark on window that we're initializing
  (window as unknown as Record<string, unknown>).__foregroundInit = { started: true, timestamp: Date.now() };

  const tryInitialize = async () => {
    try {
      // initializeMessaging() resolves as soon as isSupported() settles (fast — one
      // check, no polling) — it's null on native Android/iOS, where FCM's web SDK
      // isn't supported and push is handled by the native Capacitor plugin instead.
      // Previously this polled getMessagingInstance() up to 20x/500ms (10s) waiting
      // for a value that can never arrive on native, adding a guaranteed 10-second
      // background loop to every app launch.
      const messaging = getMessagingInstance() ?? await initializeMessaging();

      if (!messaging) {
        const msg = '[Foreground] Messaging not supported on this platform — skipping foreground handler'; // i18n-ignore: debug log
        logs.push(msg);
        console.log(msg);
        const init = (window as unknown as Record<string, unknown>).__foregroundInit as Record<string, unknown>;
        init.unsupported = true;
        return;
      }

      logs.push('[Foreground] ✅ Messaging instance ready!');
      console.log('[Foreground] ✅ Messaging instance ready!');
      logs.push('[Foreground] Registering onMessage handler...');
      console.log('[Foreground] Registering onMessage handler...');
      
      // onMessage handler intercepts foreground messages
      onMessage(messaging, async (payload) => {
        const msgLogs: string[] = [];
        msgLogs.push('[Foreground] ✅✅✅ MESSAGE RECEIVED IN FOREGROUND');
        console.log('[Foreground] ✅✅✅ MESSAGE RECEIVED IN FOREGROUND');
        (window as unknown as Record<string, unknown>).__foregroundMessageReceived = true;
        
        msgLogs.push(`[Foreground] Payload keys: ${Object.keys(payload).join(', ')}`);
        console.log('[Foreground] Payload keys:', Object.keys(payload));
        
        const title = payload.notification?.title || payload.data?.title || i18n.t('utils.foregroundNotifications.notification');
        const body = payload.notification?.body || payload.data?.body || '';
        
        msgLogs.push(`[Foreground] Final title: "${title}", body: "${body}"`);
        console.log('[Foreground] Final title:', title, 'body:', body);
        
        msgLogs.push(`[Foreground] Notification permission: ${Notification.permission}`);
        console.log('[Foreground] Notification permission:', Notification.permission);
        
        const extPayload = payload as WebPushPayload;
        const webpushNotif = extPayload.webpush?.notification || {};
        
        // CRITICAL: On Android, system notifications don't reliably show as banners.
        // Solution: Dispatch a custom event for in-app toast + try system notification.
        // This ensures users see the notification either way (in-app or system).
        msgLogs.push(`[Foreground] Dispatching in-app toast notification...`);
        console.log('[Foreground] Dispatching in-app toast notification...');
        dispatchForegroundNotification(title, body, payload.data);
        
        // For Android banner/heads-up notification, we set requireInteraction: true
        // This helps but may not work on all Android browsers due to browser/system limitations
        const notificationOptions: NotificationOptions & { vibrate?: number[]; tag?: string } = {
          body: body,
          icon: webpushNotif.icon || '/favicon.png',
          badge: webpushNotif.badge || '/favicon.png',
          tag: webpushNotif.tag || `notification-${Date.now()}`,
          // CRITICAL: requireInteraction: true = banner/heads-up on Android (best effort)
          // But Android may still show silently depending on system settings
          requireInteraction: webpushNotif.requireInteraction ?? true,
          ...(webpushNotif.vibrate && { vibrate: webpushNotif.vibrate }),
        };

        msgLogs.push(`[Foreground] Creating system notification with options...`);
        console.log('[Foreground] Notification options:', notificationOptions);

        if ('Notification' in window && Notification.permission === 'granted') {
          msgLogs.push('[Foreground] ✅ Attempting to display system notification');
          console.log('[Foreground] ✅ Attempting to display system notification');
          
          // Try Notification API first (works on desktop/some browsers)
          let notifCreated = false;
          try {
            new Notification(title, notificationOptions);
            msgLogs.push('[Foreground] ✅ Notification created via Notification API');
            console.log('[Foreground] ✅ Notification created via Notification API');
            notifCreated = true;
          } catch (notifError) {
            msgLogs.push(`[Foreground] Notification API failed: ${String(notifError)}`);
            console.error('[Foreground] Notification API failed:', notifError);
          }
          
          // If Notification API failed, fall back to service worker method
          if (!notifCreated) {
            msgLogs.push('[Foreground] Falling back to ServiceWorkerRegistration.showNotification()...');
            console.log('[Foreground] Falling back to ServiceWorkerRegistration.showNotification()...');
            
            try {
              const reg = await navigator.serviceWorker.ready;
              
              if (!reg) {
                msgLogs.push('[Foreground] ❌ No service worker registration');
              } else if (!reg.active) {
                msgLogs.push('[Foreground] ❌ Service worker not active');
              } else {
                msgLogs.push('[Foreground] Calling showNotification()...');
                await reg.showNotification(title, notificationOptions);
                msgLogs.push('[Foreground] ✅ showNotification() succeeded (may show silently on Android)');
                console.log('[Foreground] ✅ showNotification() succeeded');
              }
            } catch (swError) {
              msgLogs.push(`[Foreground] ❌ showNotification() error: ${String(swError)}`);
              console.error('[Foreground] showNotification() error:', swError);
            }
          }
        } else {
          msgLogs.push(`[Foreground] ❌ Cannot display: Notification API unavailable or permission not granted`);
        }
        
        // Badge will be updated automatically by real-time Firestore listener
        // when the notification document is created
        
        msgLogs.push(`[Foreground] ✅ In-app toast should be visible at top of screen (if app is open)`);
      });
      
      logs.push('[Foreground] ✅ onMessage handler registered successfully!');
      console.log('[Foreground] ✅ onMessage handler registered successfully!');
      const init = (window as unknown as Record<string, unknown>).__foregroundInit as Record<string, unknown>;
      init.success = true;
    } catch (error) {
      logs.push(`[Foreground] ❌ Error: ${String(error)}`);
      console.error('[Foreground] ❌ Error:', error);
      const init = (window as unknown as Record<string, unknown>).__foregroundInit as Record<string, unknown>;
      init.error = String(error);
    }
  };
  
  // Start the initialization
  tryInitialize();
}

/**
 * Listen for badge update messages from the service worker
 * The service worker sends messages when it receives background notifications
 */
if (navigator.serviceWorker) {
  navigator.serviceWorker.addEventListener('message', async (event) => {
    if (event.data && event.data.type === 'INCREMENT_BADGE') {
      console.log('[Foreground] Received INCREMENT_BADGE message from service worker');
      try {
        if ('setAppBadge' in navigator) {
          // Read current count and increment
          let currentCount = parseInt(localStorage.getItem('badgeCount') || '0', 10);
          currentCount = Math.max(1, currentCount + 1);
          localStorage.setItem('badgeCount', currentCount.toString());
          
          const nav = navigator as unknown as { setAppBadge: (count: number) => Promise<void> };
          await nav.setAppBadge(currentCount);
          console.log('[Foreground] ✅ Badge incremented to', currentCount, 'via service worker message');
        }
      } catch (error) {
        console.log('[Foreground] Error incrementing badge from service worker message:', error);
      }
    } else if (event.data && event.data.type === 'SET_BADGE') {
      console.log('[Foreground] Received SET_BADGE message from service worker:', event.data.count);
      try {
        if ('setAppBadge' in navigator) {
          const nav = navigator as unknown as { setAppBadge: (count: number) => Promise<void> };
          await nav.setAppBadge(event.data.count);
          localStorage.setItem('badgeCount', event.data.count.toString());
          console.log('[Foreground] ✅ Badge set to', event.data.count, 'via service worker message');
        }
      } catch (error) {
        console.log('[Foreground] Error setting badge from service worker message:', error);
      }
    } else if (event.data && event.data.type === 'CLEAR_BADGE') {
      console.log('[Foreground] Received CLEAR_BADGE message from service worker');
      try {
        if ('clearAppBadge' in navigator) {
          const nav = navigator as unknown as { clearAppBadge: () => Promise<void> };
          await nav.clearAppBadge();
          localStorage.setItem('badgeCount', '0');
          console.log('[Foreground] ✅ Badge cleared via service worker message');
        }
      } catch (error) {
        console.log('[Foreground] Error clearing badge from service worker message:', error);
      }
    }
  });
}